import { QuoteIcon } from '@euler/app/components/icons/QuoteIcon';
import { getRatingLabel, Rating } from '@euler/app/components/Rating';
import { Typography } from '@euler/app/components/typography';
import { TaskDetailSection } from '@euler/app/flows/task/pages/detail/components/TaskDetailSection';
import { Colors, StatusColors } from '@euler/components/Colors';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { VehicleServiceComment } from '@euler/model';
import { Maybe } from '@euler/types';
import { formatDateTime } from '@euler/utils/formatters';
import { memo } from 'react';
import { StyleProp, Text, View, ViewStyle } from 'react-native';

export const ServiceRecordCommentSection = memo(
  ({ comment }: { comment: Maybe<VehicleServiceComment> }) => {
    const { rpx } = useResponsiveContext();
    const hasCommentContent = Boolean(comment?.comment?.trim());
    const showText = (comment != null && hasCommentContent) || !comment;

    return (
      <TaskDetailSection
        title="服务评价"
        noBodyPadding
        headerStyle={comment == null ? undefined : { borderBottomWidth: 0 }}
        headerRightTextStyle={{ color: Colors.Gray2, fontSize: rpx(12) }}
        headerRight={
          comment?.timestamp
            ? `评价时间: ${formatDateTime(comment.timestamp)}`
            : undefined
        }
      >
        {comment != null ? (
          <>
            <RatingCell
              score={comment.serviceAttitudeScore}
              title="服务态度评分"
              labelType="satisfaction"
            />
            <RatingCell
              score={comment.serviceEfficiencyScore}
              title="服务效率评分"
              labelType="satisfaction"
            />
            <RatingCell
              score={comment.professionalDegreeScore}
              title="专业程度评分"
              labelType="satisfaction"
            />
            <RatingCell
              score={comment.satisfactionDegreeScore}
              title="本服务满意度"
              labelType="satisfaction"
            />
            <RatingCell
              score={comment.score}
              title="综合服务评价"
              labelType="satisfaction"
            />
          </>
        ) : null}
        {showText ? (
          <HStack
            alignItems="stretch"
            css={`
              padding: ${rpx(15)}px;
            `}
          >
            {hasCommentContent ? (
              <QuoteIcon
                size={rpx(32)}
                css={`
                  margin-right: ${rpx(10)}px;
                  top: ${rpx(-8)}px;
                `}
              />
            ) : null}
            <Text
              css={`
                font-family: ${hasCommentContent
                  ? FontFamily.NotoSans.Regular
                  : FontFamily.NotoSans.Light};
                font-size: ${rpx(hasCommentContent ? 13 : 15)}px;
                color: ${hasCommentContent ? Colors.Gray3 : Colors.Gray3};
                flex: 1;
              `}
            >
              {comment == null
                ? '暂无评价'
                : // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                  comment?.comment?.trim() || '无评语'}
            </Text>
          </HStack>
        ) : null}
      </TaskDetailSection>
    );
  },
);

const RatingCell = memo(
  ({
    title,
    score,
    style,
    labelType,
  }: {
    title: string;
    first?: boolean;
    score: number;
    labelType?: 'default' | 'satisfaction';
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <HStack
        justifyContent="space-between"
        css={`
          padding: ${rpx(10)}px ${rpx(15)}px;
          border-top-width: 1px;
          border-top-color: #e7e7e7;
        `}
        style={style}
      >
        <View
          css={`
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
          `}
        >
          <Typography.Label regular size={rpx(14)}>
            {title}
          </Typography.Label>
          <Rating
            value={score}
            maxValue={10}
            iconSize={rpx(18)}
            css={`
              margin-left: ${rpx(15)}px;
            `}
          />
        </View>
        <HStack justifyContent="flex-end">
          <Typography.Label
            medium
            size={rpx(14)}
            css={`
              margin-left: ${rpx(8)}px;
              color: ${score >= 8
                ? StatusColors.Success
                : score > 6
                ? StatusColors.Warn
                : StatusColors.Danger};
            `}
          >
            {getRatingLabel({ value: score / 2, type: labelType })}
          </Typography.Label>
        </HStack>
      </HStack>
    );
  },
);
